import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import DashPayments from "../../features/dashboard/payments";
import useAuth from "../../hooks/useAuth";
import { CommonBody, Permission } from "../../types/General";
import { USER_ROLES } from "../../utils/enums";
import {
  GetDashboardResponse,
  useGetDashboardMutation,
} from "../../service/payments";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { errorToast } from "../../helpers";
import { PriceFormatter } from "../../utils/commoFunctions";
import dayjs from "dayjs";

const Dashboard = () => {
  const userData = useAuth();
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [hidePermission, setHidePermission] = useState<Permission>();
  const [anchorEl1, setAnchorEl1] = React.useState<null | HTMLElement>(null);
  const [dashBoardResponse, setDashboardResponse] =
    useState<GetDashboardResponse | null>(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDateInputs, setShowDateInputs] = useState(false);

  const today = dayjs().format("YYYY-MM-DD");

  const [getDashboard] = useGetDashboardMutation();

  const setRange = (range: string) => {
    const today = dayjs();
    let from = "";
    const to = today.format("YYYY-MM-DD");

    switch (range) {
      case "last_week":
        from = today.subtract(7, "day").format("YYYY-MM-DD");
        break;
      case "last_month":
        from = today.subtract(1, "month").format("YYYY-MM-DD");
        break;
      case "last_year":
        from = today.subtract(1, "year").format("YYYY-MM-DD");
        break;
    }

    setStartDate(from);
    setEndDate(to);
    getDashboardData(from, to);
    setShowDateInputs(false);
  };

  const open1 = Boolean(anchorEl1);
  const handleClick1 = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl1(event.currentTarget);
  };
  const handleClose1 = () => {
    setAnchorEl1(null);
  };
  const stats = {
    account: [
      {
        title: "Total revenue",
        value: dashBoardResponse?.revenue
          ? PriceFormatter(dashBoardResponse?.revenue?.toString())
          : 0,
        route: "/manage-payments/payments",
      },
      { title: "Total expenses", value: "-", route: "" },
      { title: "Total amount in the bank", value: "-", route: "" },
      { title: "Total cash", value: "-", route: "" },
    ],
    applications: [
      {
        title: "Total number of active applications",
        value: dashBoardResponse?.active,
        route: "/manage-applications",
      },
      {
        title: "Total number of inactive applications",
        value: dashBoardResponse?.inactive,
        route: "/manage-applications",
      },
      {
        title: "Total number of in-progress applications",
        value: dashBoardResponse?.in_progress,
        route: "/manage-applications",
      },
      {
        title: "Total number of completed applications",
        value: dashBoardResponse?.completed,
        route: "/manage-applications",
      },
    ],
    employees: [
      { title: "On-time employees", value: "-", route: "" },
      { title: "Late arrival", value: "-", route: "" },
      { title: "Absent employees", value: "-", route: "" },
      { title: "Total leaves", value: "-", route: "/manage-leaves" },
      { title: "Approved leaves", value: "-", route: "/manage-leaves" },
      { title: "Pending leaves", value: "-", route: "/manage-leaves" },
    ],
  };

  const checkPermission = () => {
    const permission = userData?.role_permission;
    if (permission?.length) {
      const foundPermission = permission?.find(
        (ele: Permission) => ele?.module === "Dashboard"
      );

      if (foundPermission) {
        setHidePermission(foundPermission);
      } else {
        navigate(-1);
      }
    }
  };

  const getDashboardData = async (startDate?: string, endDate?: string) => {
    try {
      const body =
        startDate?.length && endDate?.length
          ? { from_date: startDate, to_date: endDate }
          : {};

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await getDashboard(encryptedBody).unwrap();

      if (response?.status === 200) {
        setDashboardResponse(response?.data);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
    }
  };
  const handleStartDateChange = (e: { target: { value: any } }) => {
    const value = e.target.value;

    if (value > today) return; // Prevent selecting future date
    if (endDate && value > endDate) return; // Prevent startDate after endDate

    setStartDate(value);
  };

  const handleEndDateChange = (e: { target: { value: any } }) => {
    const value = e.target.value;

    if (value > today) return; // Prevent selecting future date
    if (startDate && value < startDate) {
      errorToast("End date should be greater than start date");
      return;
    } // Prevent endDate before startDate

    setEndDate(value);
  };

  useEffect(() => {
    getDashboardData(startDate, endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    if (userData?.user_role_type === USER_ROLES.SUBADMIN) {
      checkPermission();
    }
  }, [userData]);

  useEffect(() => {
    if (typeof hidePermission !== "undefined") {
      if (!hidePermission?.can_view) {
        navigate("/permission-denied", { replace: true });
      }
    }
  }, [hidePermission, userData]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Dashboard</h1>
      </div>

      <Box className="stats_tabs">
        <div className="fdx">
          <div className="left_s">
            <Tabs
              value={tabIndex}
              onChange={(_, newValue) => setTabIndex(newValue)}
            >
              <Tab label="Accountant" />
              <Tab label="Applications" />
              <Tab label="Employees" />
            </Tabs>
          </div>
          <div className="right_s">
            <Box sx={{ display: "flex", gap: "20px" }}>
              {showDateInputs ? (
                <>
                  <TextField
                    className="dashInput"
                    type="date"
                    variant="outlined"
                    placeholder="Start Date"
                    fullWidth
                    hiddenLabel
                    value={startDate}
                    onChange={handleStartDateChange}
                    inputProps={{ max: today }}
                  />
                  <TextField
                    className="dashInput"
                    type="date"
                    variant="outlined"
                    placeholder="End Date"
                    fullWidth
                    hiddenLabel
                    inputProps={{ max: today }}
                    value={endDate}
                    onChange={handleEndDateChange}
                  />
                </>
              ) : null}
              <Button
                className="btn btn_blue dash_btn"
                aria-controls={open1 ? "basic-menu1" : undefined}
                aria-haspopup="true"
                aria-expanded={open1 ? "true" : undefined}
                onClick={handleClick1}
                sx={{ background: "white", minWidth: "100px" }}
              >
                <img src="/static/images/filter_icon.svg" alt="Icon" /> Filter
              </Button>
              <Menu
                className="filter_menu"
                id="basic-menu1"
                anchorEl={anchorEl1}
                open={open1}
                onClose={handleClose1}
                MenuListProps={{
                  "aria-labelledby": "basic-button2",
                }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    boxShadow: "0px 4px 30px 0px rgba(0, 0, 0, 0.13)",
                    borderRadius: "10px",
                    mt: 1,
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem className="export_btns v2">
                  <Button
                    onClick={() => {
                      setShowDateInputs(true);
                      handleClose1();
                    }}
                  >
                    Custom
                  </Button>
                  <Button
                    onClick={() => {
                      setRange("last_week");
                      handleClose1();
                    }}
                  >
                    Last Week
                  </Button>
                  <Button
                    onClick={() => {
                      setRange("last_month");
                      handleClose1();
                    }}
                  >
                    Last Month
                  </Button>
                  <Button
                    onClick={() => {
                      setRange("last_year");
                      handleClose1();
                    }}
                  >
                    Last Year
                  </Button>
                </MenuItem>
              </Menu>
              {/* {startDate?.length && endDate?.length ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (startDate && endDate) {
                      getDashboardData(startDate, endDate);
                    } else {
                      getDashboardData(); // default - total applications
                    }
                  }}
                  disabled={loading}
                >
                  Apply Filter
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    getDashboardData();
                  }}
                  disabled={loading}
                >
                  Clear Filter
                </Button>
              )} */}
            </Box>
          </div>
        </div>
        {tabIndex === 0 ? (
          <Box sx={{ marginTop: 2 }} className="stats_boxes gap_m">
            {stats.account.map((stat, index) => (
              <div
                className="cards"
                onClick={() => navigate(stat?.route)}
                key={index}
              >
                <figure>
                  <img src="/static/images/download_icon.svg" alt="" />
                </figure>
                <h2>{stat.value}</h2>
                <p>{stat.title}</p>
              </div>
            ))}
          </Box>
        ) : null}

        {tabIndex === 1 ? (
          <Box sx={{ marginTop: 2 }} className="stats_boxes gap_m">
            {stats.applications.map((stat) => (
              <div className="cards" onClick={() => navigate(stat?.route)}>
                <figure>
                  <img src="/static/images/download_icon.svg" alt="" />
                </figure>
                <h2>{stat.value}</h2>
                <p>{stat.title}</p>
              </div>
            ))}
          </Box>
        ) : null}

        {tabIndex === 2 ? (
          <Box sx={{ marginTop: 2 }} className="stats_boxes gap_m">
            {stats.employees.map((stat) => (
              <div className="cards" onClick={() => navigate(stat?.route)}>
                <figure>
                  <img src="/static/images/download_icon.svg" alt="" />
                </figure>
                <h2>{stat.value}</h2>
                <p>{stat.title}</p>
              </div>
            ))}
          </Box>
        ) : null}
      </Box>

      <div className="grid_sc gap_m ">
        <DashPayments />
      </div>
    </MainContainer>
  );
};

export default Dashboard;
